import { Log, send } from '@openticket/lib-log';
import urlJoin from 'url-join';
import { ShopWidgetExampleError } from './utils/errors/ShopWidgetExampleError';

interface ExampleTicket {
    guid: string;
    count: number;

    element: HTMLDivElement;
    plusButton: HTMLAnchorElement;
    minButton: HTMLAnchorElement;
    counter: HTMLDivElement;
}

class Example {

    private whitelabelLogoUrl: string = '';
    private whitelabelSettings: Record<string, unknown> = {};
    private fallbackSettings: Record<string, unknown> = {};
    private tickets: string[] = [];

    async init(url: Location) {
        const removeLowestLeafFromHostname = (hostname: string): string => {
            if (hostname.split('.').length <= 2) {
                return hostname;
            }
            return hostname.replace(/^[^.]+\./, '');
        };

        if (import.meta.env.VITE_WHITELABEL_CONFIG_URL) {
            const whitelabelPrefix: string = import.meta.env.VITE_WHITELABEL_CONFIG_URL.replace(/\/+$/, '');
            this.whitelabelSettings = await this.getWhitelabelConfig(urlJoin(whitelabelPrefix, 'config.json'));
            this.whitelabelLogoUrl = urlJoin(whitelabelPrefix, 'graphics', 'logo.svg');
        } else {
            const whitelabelHostname: string = removeLowestLeafFromHostname(url.hostname).replace(/\/+$/, '');
            this.whitelabelSettings = await this.getWhitelabelConfigFromHostname(whitelabelHostname);
            this.whitelabelLogoUrl = `https://cdn.openticket.tech/whitelabels/${whitelabelHostname}/graphics/logo.svg`;
        }

        this.fallbackSettings = await this.getWhitelabelConfigFromHostname('openticket.tech');

        const shopData = await this.getShopData(this.getShopApiUrl(), this.getShopId());
        if (shopData && 'tickets' in shopData && shopData.tickets && typeof shopData.tickets === 'object') {
            this.tickets.push(...Object.keys(shopData.tickets));
        }
    }

    async getWhitelabelConfigFromHostname(hostname: string): Promise<Record<string, unknown>> {
        return this.getWhitelabelConfig(`https://cdn.openticket.tech/whitelabels/${hostname}/config.json`);
    }

    async getWhitelabelConfig(url: string): Promise<Record<string, unknown>> {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                send(
                    new ShopWidgetExampleError({
                        slug: 'shop.error.shop_widget.example.whitelabel.invalid',
                        message: `Invalid response from whitelabel settings for ${url}`,
                    }),
                    Log.Trace,
                );
            } else {
                return await response.json() as Record<string, unknown>;
            }
        } catch (error) {
            send(
                new ShopWidgetExampleError({
                    slug: 'shop.error.shop_widget.example.whitelabel.failed_loading',
                    message: `Could not load whitelabel settings for ${url}`,
                }),
                Log.Trace,
            );
        }
        return {};
    }

    async getShopData(url: string, guid: string): Promise<Record<string, unknown>> {
        const shopDataUrl = `${url}/${guid}/data`;
        const response = await fetch(shopDataUrl);
        if (!response.ok) {
            send(
                new ShopWidgetExampleError({
                    slug: 'shop.error.shop_widget.example.invalid_shop_data',
                    message: 'Invalid shop data response',
                    shop_id: guid,
                }),
                Log.Trace,
            );
        } else {
            return await response.json() as Record<string, unknown>;
        }
        return {};
    }

    readAndApplyExampleParam(exampleEncoded: string | undefined) {
        if (!exampleEncoded) {
            return;
        }

        const example = JSON.parse(atob(exampleEncoded)) as {
            vars?: Record<string, string>,
            css?: Record<string, string>
        };

        if (example.vars) {
            Object.entries(example.vars).forEach(([ key, value ]) => {
                document.documentElement.style.setProperty(key, value);
            });
        }

        if (example.css) {
            let styleContents = '';
            Object.entries(example.css).forEach(([ selector, styles ]) => {
                styleContents += `${selector} {\n`;
                Object.entries(styles).forEach(([ key, value ]) => {
                    styleContents += `\t${key}: ${value};\n`;
                });
                styleContents += '}\n';
            });

            const styleSheet = document.createElement('style');
            styleSheet.textContent = styleContents;
            document.head.appendChild(styleSheet);
        }
    }

    generateTicketColumn(ticketGuid: string, name: string): ExampleTicket {
        const ticketEl = document.createElement('div');
        ticketEl.classList.add('ticket');
        ticketEl.id = `ticket-${ticketGuid}`;
        ticketEl.insertAdjacentHTML('beforeend', `<h1>${name}</h1>`);

        const ticketWrapperEl = document.createElement('div');
        ticketWrapperEl.append(ticketEl);

        const ticketSubEl = document.createElement('a');
        ticketSubEl.id = `ticket-${ticketGuid}-min-button`;
        ticketSubEl.classList.add('button', 'icon-button');
        ticketSubEl.innerText = '-';
        const ticketCountEl = document.createElement('div');
        ticketCountEl.id = `ticket-${ticketGuid}-count`;
        ticketCountEl.classList.add('ticket-count');
        ticketCountEl.innerText = '0';
        const ticketAddEl = document.createElement('a');
        ticketAddEl.id = `ticket-${ticketGuid}-plus-button`;
        ticketAddEl.classList.add('button', 'icon-button');
        ticketAddEl.innerText = '+';

        const controlsEl = document.createElement('div');
        controlsEl.classList.add('ticket-controls');
        controlsEl.append(ticketSubEl, ticketCountEl, ticketAddEl);
        ticketEl.appendChild(controlsEl);

        return {
            count: 0,
            guid: ticketGuid,

            element: ticketWrapperEl,
            counter: ticketCountEl,
            plusButton: ticketAddEl,
            minButton: ticketSubEl,
        };
    }

    getLogoUrl(): string {
        return this.whitelabelLogoUrl;
    }

    getShopUrl(): string {
        return this.getSetting([ 'shop', 'url' ]).replace(/\/$/, '');
    }

    getShopApiUrl(): string {
        return this.getSetting([ 'shop', 'api_url' ]).replace(/\/$/, '');
    }

    getShopId(): string {
        const shopId: string = this.getSetting([ 'shop', 'example_shop_guid' ]);

        if (!shopId) {
            throw send(
                new ShopWidgetExampleError({
                    slug: 'shop.error.shop_widget.example.missing_shop_id',
                    message: 'Missing whitelabel settings shop id',
                }),
                Log.Trace,
            );
        }

        return shopId;
    }

    getTicketGuids(): string[] {
        return this.tickets;
    }

    getSetting(settingPath: string[], settingsObject: Record<string, unknown> = this.whitelabelSettings): string {
        const p = settingPath[0];
        if (settingsObject && p && settingsObject[p]) {
            if (settingPath.length === 1 && typeof settingsObject[p] === 'string') {
                return settingsObject[p];
            }
            if (typeof settingsObject[p] === 'object') {
                return this.getSetting(settingPath.slice(1), settingsObject[p] as Record<string, unknown>);
            }
        }
        if (settingsObject !== this.fallbackSettings) {
            return this.getSetting(settingPath, this.fallbackSettings);
        }
        return '';
    }

}

const example = new Example();
Object.defineProperty(window, 'example', {
    get: () => example,
    set: () => {
        console.warn('Re-setting [example] is not allowed, ignoring.');

        send(
            new ShopWidgetExampleError({
                slug: 'shop.error.shop_widget.example.resetting_not_allowed',
                message: 'Re-setting [example] is not allowed, ignoring.',
            }),
            Log.Warn,
        );
    },
});
